'use client';

import React from 'react';
import { useTheme } from '../context/ThemeProvider';
import { useLocale } from '../context/DynamicLocaleProvider';
import styles from './GlobalPagination.module.css';

export interface PaginationState {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  visiblePages: number[];
}

export interface PaginationActions {
  goToPage: (page: number) => void;
  nextPage: () => void;
  previousPage: () => void;
  firstPage: () => void;
  lastPage: () => void;
}

export interface GlobalPaginationProps {
  pagination: PaginationState;
  actions: PaginationActions;
  showItemsPerPage?: boolean;
  itemsPerPageOptions?: number[];
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  className?: string;
  compact?: boolean;
}

/**
 * High-performance pagination component with optimized rendering
 * Features:
 * - Responsive design
 * - Keyboard navigation support
 * - Accessible ARIA labels
 * - Configurable page size options
 * - Compact mode for mobile
 */
export function GlobalPagination({
  pagination,
  actions,
  showItemsPerPage = false,
  itemsPerPageOptions = [6, 12, 24, 48],
  onItemsPerPageChange,
  className = '',
  compact = false
}: GlobalPaginationProps) {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { t } = useLocale();

  const {
    currentPage,
    totalPages,
    itemsPerPage,
    hasNextPage,
    hasPreviousPage,
    visiblePages
  } = pagination;

  const {
    goToPage,
    nextPage,
    previousPage,
    firstPage,
    lastPage
  } = actions;

  // Don't render if there's only one page or no items
  if (totalPages <= 1) {
    return null;
  }

  const handleKeyDown = (event: React.KeyboardEvent, action: () => void) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

  const renderPageButton = (page: number, isActive: boolean = false) => (
    <button
      key={page}
      className={`${styles.pageButton} ${isActive ? styles.active : ''}`}
      onClick={() => goToPage(page)}
      onKeyDown={(e) => handleKeyDown(e, () => goToPage(page))}
      aria-label={`${t('pagination_go_to_page')} ${page}`}
      aria-current={isActive ? 'page' : undefined}
      disabled={isActive}
    >
      {page}
    </button>
  );

  const renderEllipsis = (key: string) => (
    <span key={key} className={styles.ellipsis} aria-hidden="true">
      ...
    </span>
  );

  const renderNavigationButton = (
    onClick: () => void,
    disabled: boolean,
    icon: string,
    label: string,
    className: string = '',
    isDouble: boolean = false
  ) => (
    <button
      className={`${styles.navButtonContainer} ${disabled ? styles.disabled : ''} ${className}`}
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
    >
      {isDouble ? (
        <div className={styles.doubleChevron}>
          <img
            src={isDarkMode ? icon : icon.replace('.svg', '.black.svg')}
            alt=""
            width={8}
            height={8}
          />
          <img
            src={isDarkMode ? icon : icon.replace('.svg', '.black.svg')}
            alt=""
            width={8}
            height={8}
          />
        </div>
      ) : (
        <img
          src={isDarkMode ? icon : icon.replace('.svg', '.black.svg')}
          alt=""
          width={8}
          height={8}
        />
      )}
    </button>
  );

  return (
    <div className={`${styles.pagination} ${className} ${compact ? styles.compact : ''}`}>

      {/* Items per page selector */}
      {showItemsPerPage && onItemsPerPageChange && (
        <div className={styles.itemsPerPage}>
          <label htmlFor="items-per-page" className={styles.label}>
            {t('pagination_items_per_page')}:
          </label>

          {/* Select items per page with FilterBar styling */}
          <div className={styles.selectContainer}>
            <select
              id="items-per-page"
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
              className={styles.select}
            >
              {itemsPerPageOptions.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <img 
              src={isDarkMode ? "/assets/icons/chevron.down.svg" : "/assets/icons/chevron.down.black.svg"} 
              alt="Dropdown" 
              width={12} 
              height={12} 
              className={styles.chevronIcon}
            />
          </div>
        </div>
      )}

      {/* Pagination info 
      <div className={styles.info}>
        <span className={styles.infoText}>
          {t('pagination_showing', {
            start: ((currentPage - 1) * itemsPerPage) + 1,
            end: Math.min(currentPage * itemsPerPage, totalItems),
            total: totalItems
          })}
        </span>
      </div>

      */}

      {/* Navigation controls */}
      <div className={styles.controls}>
        {/* First page */}
        {!compact && (
          renderNavigationButton(
            firstPage,
            !hasPreviousPage,
            "/assets/icons/chevron.left.svg",
            t('pagination_first_page'),
            '',
            true
          )
        )}

        {/* Previous page */}
        {renderNavigationButton(
          previousPage,
          !hasPreviousPage,
          "/assets/icons/chevron.left.svg",
          t('pagination_previous_page')
        )}

        {/* Page numbers */}
        <div className={styles.pageNumbers}>
          {visiblePages.map((page, index) => {
            const isActive = page === currentPage;
            const showEllipsisBefore = index === 0 && page > 1;
            const showEllipsisAfter = index === visiblePages.length - 1 && page < totalPages;

            return (
              <React.Fragment key={page}>
                {showEllipsisBefore && renderEllipsis(`ellipsis-before-${page}`)}
                {renderPageButton(page, isActive)}
                {showEllipsisAfter && renderEllipsis(`ellipsis-after-${page}`)}
              </React.Fragment>
            );
          })}
        </div>

        {/* Next page */}
        {renderNavigationButton(
          nextPage,
          !hasNextPage,
          "/assets/icons/chevron.right.svg",
          t('pagination_next_page')
        )}

        {/* Last page */}
        {!compact && (
          renderNavigationButton(
            lastPage,
            !hasNextPage,
            "/assets/icons/chevron.right.svg",
            t('pagination_last_page'),
            '',
            true
          )
        )}
      </div>
    </div>
  );
}

export default GlobalPagination;
